<template>
  <div class="library-status-selector">
    <ElementSelect
      v-model="selectedStatus"
      placeholder="---"
      size="small"
      :class="[libraryStatusSelector, mapStatusToCSSClass]"
      @change="updateSelectedStatus"
    >
      <ElementTooltip
        v-for="status in mappedTransitions"
        :key="status.value"
        ref="tooltip"
        effect="dark"
        :content="`You cannot change status from ${selectedStatus} to ${status.label}.`"
        placement="right-start"
        :disabled="status.is_possible || status.value === selectedStatus"
        :open-delay="400"
        :enterable="false"
      >
        <ElementOption
          :label="status.action"
          :value="status.value"
          :disabled="!status.is_possible && status.value !== selectedStatus"
        />
      </ElementTooltip>
    </ElementSelect>
    <ElementTooltip
      v-if="size !== 'small' && item.assetStatusChangeLog"
      effect="dark"
      :content="`Changed by ${item.assetStatusChangeLog.changed_by},
          ${formatToLocaleDateShort(item.assetStatusChangeLog.changed_at)}`"
      placement="right-start"
      :open-delay="400"
      :enterable="false"
    >
      <i
        id="library-status--log-tooltip"
        class="el-icon-info"
      />
    </ElementTooltip>
    <br>
    <template v-if="size !== 'small' && item.assetStatusChangeLog">
      {{ item.assetStatusChangeLog.change_reason }}
      <td>
        <template v-if="isNotAllowedToAddVersions">
          <p class="locked-style-message">
            VENDOR - Style is CLOSED for uploads.<br>
            DO NOT change the style status.<br>
            TAKE NO ACTION until further advised.<br>
          </p>
        </template>
      </td>
    </template>
  </div>
</template>

<script>
import { ASSET_STATUS, ASSET_STATUSES_ORDER } from '@/constants/assetStatuses'
import { formatToLocaleDateShort } from '@/services/utils'
import { USER_ROLE } from '@/constants/roleType'
import { mapGetters } from 'vuex'

export default {
  name: 'LibraryStatusSelector',

  props: {
    item: Object,
    transitions: Array,
    reset: {
      type: Boolean,
      default: false,
      required: false
    },
    size: {
      type: String,
      default: 'medium',
      required: false
    }
  },

  emits: ['update-status'],

  data () {
    return {
      selectedStatus: null
    }
  },

  computed: {
    ...mapGetters({
      user: 'getCognitoUserData'
    }),

    /**
     * @returns {object}
     */
    mappedTransitions () {
      const transitions = this.transitions

      return transitions.sort((a, b) => {
        return (
          ASSET_STATUSES_ORDER.indexOf(a.value) -
          ASSET_STATUSES_ORDER.indexOf(b.value)
        )
      })
    },

    /**
     * @returns {string}
     */
    mapStatusToCSSClass () {
      switch (this.selectedStatus) {
        case ASSET_STATUS.CREATED.label:
          return 'library-status__input-created'
        case ASSET_STATUS.RESUBMIT.label:
        case ASSET_STATUS.RESUBMIT.value:
          return 'library-status__input-resubmit'
        case ASSET_STATUS.CANCELLED.label:
        case ASSET_STATUS.CANCELLED.value:
          return 'library-status__input-cancelled'
        case ASSET_STATUS.APPROVED.label:
          return 'library-status__input-approved'
        case ASSET_STATUS.REVIEW.label:
          return 'library-status__input-review'
        case ASSET_STATUS.APPROVED_FOR_DAM.label:
        case ASSET_STATUS.APPROVED_FOR_DAM.value:
          return 'library-status__input-approved-for-dam'
        case ASSET_STATUS.REQUESTED.label:
          return 'library-status__input-requested'
        case ASSET_STATUS.PENDING.label:
          return 'library-status__input-pending'
        case ASSET_STATUS.IN_WORK.label:
        case ASSET_STATUS.IN_WORK.value:
          return 'library-status__input-in-work'
        default:
          return ''
      }
    },

    /**
     * @returns {string}
     */
    libraryStatusSelector () {
      return this.size === 'small'
        ? 'library-status__selector-small'
        : 'library-status__selector'
    },

    /**
     * @returns {boolean}
     */
    isVendorUser () {
      return this.user.role === USER_ROLE.VENDOR.name
    },

    /**
     * @returns {boolean}
     */
    isNotAllowedToAddVersions () {
      return (
        this.isVendorUser &&
        [
          ASSET_STATUS.REVIEW.label,
          ASSET_STATUS.APPROVED.label,
          ASSET_STATUS.APPROVED_FOR_DAM.label,
          ASSET_STATUS.CANCELLED.label,
          ASSET_STATUS.PENDING.label
        ].includes(this.item.assetStatus)
      )
    }
  },

  watch: {
    /**
     *
     */
    item () {
      this.selectedStatus = this.item.assetStatus
    },

    /**
     *
     */
    reset () {
      this.selectedStatus = this.item.assetStatus
    }
  },

  created () {
    if (this.item !== null) {
      this.selectedStatus = this.item.assetStatus
    }
  },

  methods: {
    formatToLocaleDateShort,

    /**
     */
    updateSelectedStatus () {
      this.$emit('update-status', this.selectedStatus)
    }
  }
}
</script>

<style lang="scss" scoped>
$library-status-selector-width: 20%;

.library-status__selector {
  width: spacing(22);
  color: $white;
}

.library-status__selector-small {
  width: spacing(12.5);
  color: $white;
}
</style>

<style lang="scss">
$warning-color: #e6a23c;
$warning-bg-color: #fdf6ec;
$warning-border-color: #faecd8;

.library-status__selector input {
  color: $white;
  font-weight: $font-weight-bold;
  border-radius: spacing(2.5);
}

#library-status--log-tooltip {
  margin-left: spacing(1);
  cursor: pointer;
}

.library-status__selector-small .el-input__inner,
.library-status__selector-small .el-input__inner:focus {
  height: spacing(2) !important;
  border-color: transparent !important;
}

.library-status__selector .el-input__inner:focus {
  border-color: transparent !important;
}

.library-status__selector .el-input--suffix .el-input__inner {
  height: spacing(4);
}

.library-status__selector-small input {
  color: $white;
  font-size: spacing(1.25);
  border-radius: spacing(2.5);
}

.library-status__selector-small .el-select__caret {
  font-size: spacing(1.25) !important;
}

.library-status__selector-small .el-input__suffix {
  top: spacing(1.25) !important;
  right: spacing(1.25) !important;
}

.library-status__selector .el-input .el-select__caret {
  color: $white !important;
}

.library-status__input-approved input {
  background-color: $status-approved;
}

.library-status__input-review input {
  background-color: $status-review;
}

.library-status__input-resubmit input {
  background-color: $status-resubmit;
}

.library-status__input-cancelled input {
  background-color: $status-cancelled;
}

.library-status__input-created input {
  background-color: $status-created;
}

.library-status__input-approved-for-dam input {
  background-color: $status-approved-for-dam;
}

.library-status__input-requested input {
  background-color: $status-requested;
}

.library-status__input-pending input {
  background-color: $status-pending;
}

.library-status__input-in-work input {
  background-color: $status-in-work;
}

.locked-style-message {
  box-sizing: border-box;
  margin-top: spacing(1);
  padding: 0 spacing(1);
  color: $warning-color;
  font-size: spacing(1.25);
  white-space: pre-wrap;
  background-color: $warning-bg-color;
  border: solid 1px $warning-border-color;
}
</style>
